import styled, { css } from 'styled-components'

export interface OptionProps {
  $selected?: boolean
  $disabled?: boolean
  $hasIcon?: boolean
  $hasDescription?: boolean
}

export const Option = styled.div<OptionProps>`
  display: flex;
  align-items: ${({ $hasDescription }) =>
    $hasDescription ? 'flex-start' : 'center'};
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 0;
  width: 100%;
  min-height: ${({ $hasDescription }) => ($hasDescription ? '48px' : '36px')};

  &:hover {
    ${({ $disabled, theme }) =>
      !$disabled &&
      css`
        background: ${theme.colors.background.secondary};
      `}
  }

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.background.secondary};
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.border.focus};
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
      font-weight: ${theme.typography.fontWeight.medium};

      &:hover {
        background: ${theme.colors.primaryLight};
      }
    `}

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      opacity: 0.5;
      color: ${theme.colors.text.tertiary};
    `}
`

export const OptionIcon = styled.span.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  width: 16px;
  height: 16px;
`

export const OptionContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const OptionLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: inherit;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const OptionDescription = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`
